import { useState, useEffect } from "react";
import { getLogEntriesForRequest } from "../api";
import {
  Stack,
  Loader,
  Text,
  Badge,
  Alert,
  Accordion,
  Code,
  Group,
} from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";

// Map LogLevel enum values to colors
const LOG_LEVEL_COLORS = {
  0: "gray", // Trace
  1: "blue", // Debug
  2: "green", // Information
  3: "yellow", // Warning
  4: "red", // Error
  5: "red", // Critical
};

const LOG_LEVEL_NAMES = {
  0: "Trace",
  1: "Debug",
  2: "Information",
  3: "Warning",
  4: "Error",
  5: "Critical",
};

function LogLevelBadge({ level }) {
  return (
    <Badge color={LOG_LEVEL_COLORS[level]} size="sm" variant="light">
      {LOG_LEVEL_NAMES[level] || `${level}`}
    </Badge>
  );
}

function LogEntryItem({ entry }) {
  const hasException = !!entry.exception;
  const hasScopes = entry.scopes && entry.scopes.length > 0;
  const hasTags = entry.tags && entry.tags.length > 0;

  return (
    <Stack gap="xs" p="sm" style={{ borderBottom: "1px solid #eee" }}>
      <Group justify="space-between" align="flex-start">
        <Stack gap={4} style={{ flex: 1 }}>
          <Group gap="xs">
            <Text size="xs" c="dimmed">
              {new Date(entry.timestamp).toLocaleString()}
            </Text>
            <LogLevelBadge level={entry.level} />
            {entry.name && (
              <Badge size="xs" variant="outline">
                {entry.name}
              </Badge>
            )}
          </Group>
          <Text
            style={{
              whiteSpace: "pre-wrap",
              lineBreak: "loose",
              wordBreak: "break-word",
            }}
            size="sm"
          >
            {entry.message}
          </Text>
        </Stack>
      </Group>

      {hasTags && (
        <Group gap="xs">
          {entry.tags.map((tag, idx) => (
            <Badge key={idx} size="xs" variant="dot">
              {tag}
            </Badge>
          ))}
        </Group>
      )}

      {hasException && (
        <Alert
          icon={<IconAlertCircle size={16} />}
          color="red"
          variant="light"
          p="xs"
        >
          <Code block style={{ fontSize: "0.75rem", whiteSpace: "pre-wrap" }}>
            {entry.exception}
          </Code>
        </Alert>
      )}

      {hasScopes && (
        <Accordion variant="contained" styles={{ content: { padding: "xs" } }}>
          <Accordion.Item value="scopes">
            <Accordion.Control>
              <Text size="xs" c="dimmed">
                Scopes ({entry.scopes.length})
              </Text>
            </Accordion.Control>
            <Accordion.Panel>
              <Code block style={{ fontSize: "0.7rem", whiteSpace: "pre-wrap" }}>
                {JSON.stringify(entry.scopes, null, 2)}
              </Code>
            </Accordion.Panel>
          </Accordion.Item>
        </Accordion>
      )}
    </Stack>
  );
}

export default function LogEntryViewer({ requestId }) {
  const [isLoading, setIsLoading] = useState(true);
  const [logEntries, setLogEntries] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!requestId) return;

    setIsLoading(true);
    setError(null);

    getLogEntriesForRequest(requestId)
      .then((data) => {
        setLogEntries(data);
        setIsLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setIsLoading(false);
      });
  }, [requestId]);

  if (!requestId) {
    return null;
  }

  if (isLoading) {
    return (
      <Stack align="center" p="md">
        <Loader size="sm" />
        <Text size="sm" c="dimmed">
          Loading log entries...
        </Text>
      </Stack>
    );
  }

  if (error) {
    return (
      <Alert
        icon={<IconAlertCircle size={16} />}
        color="red"
        variant="light"
        title="Error loading log entries"
      >
        {error}
      </Alert>
    );
  }

  if (logEntries.length === 0) {
    return (
      <Text size="sm" c="dimmed" ta="center" p="md">
        No log entries found
      </Text>
    );
  }

  return (
    <Stack gap={0}>
      {/* <Text size="sm" fw={600} p="sm" pb="xs">
        Log Entries ({logEntries.length})
      </Text> */}
      {logEntries.map((entry) => (
        <LogEntryItem key={entry.id} entry={entry} />
      ))}
    </Stack>
  );
}
